import {
    faEdit,
    faFileAlt,
    faListCheck,
    faPlay,
    faRedo,
    faStop,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ConditionsStatusModal from '../../components/Conditions/ConditionsStatusModal';
import Footer from '../../components/Footer/Footer';
import LogModal from '../../components/Logs/LogModal';
import Menu from '../../components/Menu/Menu';
import MemoryTable from '../../components/Memory/MemoryTable';
import Toast from '../../components/Toast/Toast';
import {
    getAutomationStatus,
    startAutomation,
    stopAutomation,
} from '../../services/AutomationsService';
import AutomationModal from './AutomationModal/AutomationModal';
import GridModal from './GridModal/GridModal';
import CandleChart from '../Dashboard/CandleChart';

function formatAge(ms) {
    if (ms === null || ms === undefined) return 'never';
    const seconds = Math.max(1, Math.round(ms / 1000));
    if (seconds < 90) return `${seconds}s ago`;
    const minutes = Math.round(seconds / 60);
    if (minutes < 90) return `${minutes}m ago`;
    return `${Math.round(minutes / 60)}h ago`;
}

function getStatusBadge(automation) {
    if (automation.schedule)
        return automation.isActive ? (
            <span className="badge bg-info ms-1">SCHEDULED</span>
        ) : (
            <span className="badge bg-danger ms-1">STOPPED</span>
        );
    return automation.isActive ? (
        <span className="badge bg-success ms-1">RUNNING</span>
    ) : (
        <span className="badge bg-danger ms-1">STOPPED</span>
    );
}

function isGrid(automation) {
    return (
        automation.actions &&
        automation.actions.length &&
        automation.actions[0].type === 'GRID'
    );
}

const INDEX_INTERVALS_MS = {
    '1m': 60000,
    '3m': 180000,
    '5m': 300000,
    '15m': 900000,
    '30m': 1800000,
    '1h': 3600000,
    '2h': 7200000,
    '4h': 14400000,
    '6h': 21600000,
    '8h': 28800000,
    '12h': 43200000,
    '1d': 86400000,
    '3d': 259200000,
    '1w': 604800000,
    '1M': 2592000000,
};

// Same rule as the backend getShortestInterval: indexes carry the interval
// as their last token (e.g. BTCUSDT:RSI_30m), and the chart follows the
// shortest one so every condition variable is visible at that scale.
function getShortestInterval(indexes) {
    const intervals = (indexes || '')
        .split(',')
        .map(ix => {
            const ixSplit = ix.trim().split('_');
            const candidate = ixSplit[ixSplit.length - 1];
            return INDEX_INTERVALS_MS[candidate] ? candidate : null;
        })
        .filter(ix => ix);

    if (!intervals.length) return '1m';

    return intervals.sort(
        (a, b) => INDEX_INTERVALS_MS[a] - INDEX_INTERVALS_MS[b],
    )[0];
}

function AutomationStatus() {
    const navigate = useNavigate();
    const { id } = useParams();

    const [status, setStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [refresh, setRefresh] = useState(0);
    const [editAutomation, setEditAutomation] = useState({
        symbol: '',
        conditions: '',
        actions: [],
        name: '',
        indexes: '',
    });
    const [notification, setNotification] = useState({ type: '', text: '' });

    useEffect(() => {
        setIsLoading(true);
        getAutomationStatus(id)
            .then(result => {
                setStatus(result);
                setUpdatedAt(new Date());
                setIsLoading(false);
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
                setIsLoading(false);
            });
    }, [id, refresh]);

    function onReloadClick() {
        setRefresh(Date.now());
    }

    function onStartClick() {
        startAutomation(id)
            .then(() => setRefresh(Date.now()))
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            });
    }

    function onStopClick() {
        stopAutomation(id)
            .then(() => setRefresh(Date.now()))
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            });
    }

    function onEditClick() {
        if (status.automation.schedule) {
            navigate('/automations/launch/' + id);
            return;
        }
        setEditAutomation({ ...status.automation });
    }

    function getEditModalTarget() {
        return isGrid(status.automation) ? '#modalGrid' : '#modalAutomation';
    }

    function getEngineAlert() {
        const engine = status && status.engine;
        if (!engine) return <></>;

        return engine.ok ? (
            <div className="alert alert-success d-flex justify-content-between py-2">
                <span>
                    Engine running. Last evaluation{' '}
                    {formatAge(engine.evaluationAgeMs)}.
                </span>
                {engine.evalErrorStreak > 0 ? (
                    <span className="fw-bold">
                        {engine.evalErrorStreak} evaluation errors in a row!
                    </span>
                ) : (
                    <></>
                )}
            </div>
        ) : (
            <div className="alert alert-danger py-2">
                Engine stalled or Redis unreachable. Last market event{' '}
                {formatAge(engine.marketEventAgeMs)}. Orders and automations may
                not be running!
            </div>
        );
    }

    function getKeysAlert() {
        const automation = status && status.automation;
        if (!automation || !automation.missingKeys) return <></>;

        const label = automation.missingKeys === 'futures' ? 'Futures' : 'Spot';
        return (
            <div className="alert alert-warning py-2">
                This automation cannot place orders: your{' '}
                <strong>{label} API key</strong> is not configured.{' '}
                <button
                    type="button"
                    className="btn btn-link p-0 align-baseline fw-bold"
                    onClick={() => navigate('/settings')}
                >
                    Configure it in Settings
                </button>{' '}
                to resume trading.
            </div>
        );
    }

    function getConfigFields(automation) {
        return (
            <>
                <div className="col-md-3 mb-3">
                    <div className="form-group">
                        <label>Symbol:</label>
                        <div>{automation.symbol}</div>
                    </div>
                </div>
                <div className="col-md-3 mb-3">
                    <div className="form-group">
                        <label>Schedule:</label>
                        <div>
                            {automation.schedule
                                ? new Date(automation.schedule).toLocaleString()
                                : '-'}
                        </div>
                    </div>
                </div>
                <div className="col-md-3 mb-3">
                    <div className="form-group">
                        <label>Conditions:</label>
                        <div className="text-break">
                            {automation.conditions || '-'}
                        </div>
                        {status && status.clauses && status.clauses.length ? (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-gray-800 mt-2"
                                title="See how close each condition is to being met"
                                data-bs-toggle="modal"
                                data-bs-target="#modalConditions"
                            >
                                <FontAwesomeIcon
                                    icon={faListCheck}
                                    className="icon icon-xs me-1"
                                />
                                Status
                            </button>
                        ) : (
                            <></>
                        )}
                    </div>
                </div>
                <div className="col-md-3 mb-3">
                    <div className="form-group">
                        <label>Indexes:</label>
                        <div>
                            {automation.indexes
                                ? automation.indexes
                                      .split(',')
                                      .filter(index => index.trim())
                                      .map(index => (
                                          <span
                                              className="badge bg-secondary me-1 mb-1"
                                              key={index}
                                          >
                                              {index.trim()}
                                          </span>
                                      ))
                                : '-'}
                        </div>
                    </div>
                </div>
                <div className="col-md-3 mb-3">
                    <div className="form-group">
                        <label>Actions:</label>
                        <div>
                            {automation.actions && automation.actions.length
                                ? automation.actions.map(action => (
                                      <span
                                          className="badge bg-secondary me-1 mb-1"
                                          key={action.id}
                                      >
                                          {action.type}
                                      </span>
                                  ))
                                : '-'}
                        </div>
                    </div>
                </div>
                <div className="col-md-3 mb-3">
                    <div className="form-group">
                        <label>Logs:</label>
                        <div>{automation.logs ? 'Enabled' : 'Disabled'}</div>
                    </div>
                </div>
                <div className="col-md-3 mb-3">
                    <div className="form-group">
                        <label>Created:</label>
                        <div>
                            {new Date(automation.createdAt).toLocaleString()}
                        </div>
                    </div>
                </div>
                <div className="col-md-3 mb-3">
                    <div className="form-group">
                        <label>Updated:</label>
                        <div>
                            {new Date(automation.updatedAt).toLocaleString()}
                        </div>
                    </div>
                </div>
            </>
        );
    }

    function getOrdersCard() {
        const orders = (status && status.orders) || [];

        return (
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card border-0 shadow">
                        <div className="card-header">
                            <div className="row align-items-center">
                                <div className="col">
                                    <h2 className="fs-5 fw-0 bold">
                                        Recent orders
                                    </h2>
                                </div>
                            </div>
                        </div>
                        <div className="card-body">
                            {!orders.length ? (
                                <p className="text-gray-600 mb-0">
                                    No orders executed by this automation yet.
                                </p>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table align-items-center table-flush">
                                        <thead className="thead-light">
                                            <tr>
                                                <th
                                                    className="border-bottom"
                                                    scope="col"
                                                >
                                                    Side
                                                </th>
                                                <th
                                                    className="border-bottom"
                                                    scope="col"
                                                >
                                                    Type
                                                </th>
                                                <th
                                                    className="border-bottom"
                                                    scope="col"
                                                >
                                                    Quantity
                                                </th>
                                                <th
                                                    className="border-bottom"
                                                    scope="col"
                                                >
                                                    Avg Price
                                                </th>
                                                <th
                                                    className="border-bottom"
                                                    scope="col"
                                                >
                                                    Status
                                                </th>
                                                <th
                                                    className="border-bottom"
                                                    scope="col"
                                                >
                                                    Time
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {orders.map(order => (
                                                <tr key={order.id}>
                                                    <td>
                                                        <span
                                                            className={
                                                                'badge ' +
                                                                (order.side ===
                                                                'BUY'
                                                                    ? 'bg-success'
                                                                    : 'bg-danger')
                                                            }
                                                        >
                                                            {order.side}
                                                        </span>
                                                    </td>
                                                    <td className="text-gray-800">
                                                        {order.type}
                                                    </td>
                                                    <td className="text-gray-600">
                                                        {order.quantity}
                                                    </td>
                                                    <td className="text-gray-600">
                                                        {order.avgPrice || '-'}
                                                    </td>
                                                    <td className="text-gray-600">
                                                        {order.status}
                                                    </td>
                                                    <td className="text-gray-600">
                                                        {order.transactTime
                                                            ? new Date(
                                                                  order.transactTime,
                                                              ).toLocaleString()
                                                            : '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <Menu />
            <main className="content">
                <div className="card card-body border-0 shadow mb-4 mt-4">
                    <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                        <div className="d-block mb-4 mb-md-0">
                            <h2 className="h4">
                                {status
                                    ? status.automation.name +
                                      ' (' +
                                      status.automation.symbol +
                                      ')'
                                    : ''}
                                {status ? (
                                    getStatusBadge(status.automation)
                                ) : (
                                    <></>
                                )}
                            </h2>
                            {updatedAt ? (
                                <small className="text-gray-500">
                                    Updated at {updatedAt.toLocaleTimeString()}
                                </small>
                            ) : (
                                <></>
                            )}
                        </div>
                        <div className="btn-toolbar mb-2 mb-md-0">
                            <div className="d-inline-flex align-items-center">
                                <button
                                    type="button"
                                    className="btn btn-gray-800 d-inline-flex align-items-center me-2"
                                    onClick={onReloadClick}
                                    disabled={isLoading}
                                    title="Reload the current data"
                                >
                                    <FontAwesomeIcon
                                        icon={faRedo}
                                        className={
                                            'icon icon-xs me-2' +
                                            (isLoading ? ' fa-spin' : '')
                                        }
                                    />
                                    Reload
                                </button>
                                {status && !status.automation.isActive ? (
                                    <button
                                        type="button"
                                        className="btn btn-success me-2"
                                        onClick={onStartClick}
                                    >
                                        <FontAwesomeIcon
                                            icon={faPlay}
                                            className="icon icon-xs me-1"
                                        />
                                        Start
                                    </button>
                                ) : (
                                    <></>
                                )}
                                {status && status.automation.isActive ? (
                                    <button
                                        type="button"
                                        className="btn btn-danger me-2"
                                        onClick={onStopClick}
                                    >
                                        <FontAwesomeIcon
                                            icon={faStop}
                                            className="icon icon-xs me-1"
                                        />
                                        Stop
                                    </button>
                                ) : (
                                    <></>
                                )}
                                {status ? (
                                    <button
                                        type="button"
                                        className="btn btn-secondary me-2"
                                        title="Edit this Automation"
                                        {...(status.automation.schedule
                                            ? {}
                                            : {
                                                  'data-bs-toggle': 'modal',
                                                  'data-bs-target':
                                                      getEditModalTarget(),
                                              })}
                                        onClick={onEditClick}
                                    >
                                        <FontAwesomeIcon
                                            icon={faEdit}
                                            className="icon icon-xs me-1"
                                        />
                                        Edit
                                    </button>
                                ) : (
                                    <></>
                                )}
                                {status && status.automation.logs ? (
                                    <button
                                        type="button"
                                        className="btn btn-info me-2"
                                        title="Logs from this Automation"
                                        data-bs-toggle="modal"
                                        data-bs-target="#modalLogs"
                                    >
                                        <FontAwesomeIcon
                                            icon={faFileAlt}
                                            className="icon icon-xs me-1"
                                        />
                                        Logs
                                    </button>
                                ) : (
                                    <></>
                                )}
                            </div>
                        </div>
                    </div>
                    {status ? (
                        <>
                            {getEngineAlert()}
                            {getKeysAlert()}
                            <div className="form-group mb-3">
                                <div className="row">
                                    {getConfigFields(status.automation)}
                                </div>
                            </div>
                        </>
                    ) : (
                        <></>
                    )}
                </div>
                {status ? (
                    <MemoryTable
                        memory={status.memory}
                        emptyText={
                            !status.automation.isActive
                                ? 'No data in the memory yet. The automation is stopped, start it to feed the memory.'
                                : 'No data in the memory yet. Wait for the next market event.'
                        }
                    />
                ) : (
                    <></>
                )}
                {status ? getOrdersCard() : <></>}
                {status &&
                status.automation.symbol &&
                status.automation.symbol !== '*' ? (
                    <CandleChart
                        symbol={status.automation.symbol}
                        interval={getShortestInterval(
                            status.automation.indexes,
                        )}
                    />
                ) : (
                    <></>
                )}
                <Footer />
            </main>
            <AutomationModal data={editAutomation} onSubmit={onReloadClick} />
            <GridModal data={editAutomation} onSubmit={onReloadClick} />
            <ConditionsStatusModal automationId={id} status={status} />
            <LogModal file={id ? 'A' + id : ''} />
            <Toast
                type={notification.type}
                text={notification.text}
                onShow={() => setNotification({})}
            />
        </>
    );
}

export default AutomationStatus;
